import { z } from 'zod';

export const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'] as const;

export type MealType = (typeof mealTypes)[number];

export const nutritionSourceKinds = [
  'manual',
  'usda',
  'web-search',
  'food-label',
  'ai-estimate',
  'recipe',
] as const;

export type NutritionSourceKind = (typeof nutritionSourceKinds)[number];

export const confidenceLevels = ['high', 'medium', 'low'] as const;

export type ConfidenceLevel = (typeof confidenceLevels)[number];

const trimmedTextSchema = z.string().trim().min(1);
const positiveNumberSchema = z.number().finite().positive();
const nonNegativeNumberSchema = z.number().finite().nonnegative();

export const macroTotalsSchema = z.object({
  calories: nonNegativeNumberSchema,
  proteinGrams: nonNegativeNumberSchema,
  carbsGrams: nonNegativeNumberSchema,
  fatGrams: nonNegativeNumberSchema,
  fiberGrams: nonNegativeNumberSchema.optional(),
  sugarGrams: nonNegativeNumberSchema.optional(),
  sodiumMilligrams: nonNegativeNumberSchema.optional(),
});

export type MacroTotals = z.infer<typeof macroTotalsSchema>;

export const canonicalQuantitySchema = z.object({
  amount: positiveNumberSchema,
  unit: trimmedTextSchema,
});

export type CanonicalQuantity = z.infer<typeof canonicalQuantitySchema>;

export const canonicalTimestampSchema = trimmedTextSchema.refine(
  (value) => !Number.isNaN(new Date(value).valueOf()),
  'Invalid timestamp',
);

const canonicalNutritionEntryFields = {
  originalInput: trimmedTextSchema,
  normalizedName: trimmedTextSchema,
  inputQuantity: canonicalQuantitySchema,
  canonicalQuantity: canonicalQuantitySchema,
  macros: macroTotalsSchema,
  source: z.enum(nutritionSourceKinds),
  confidence: z.enum(confidenceLevels),
  consumedAt: canonicalTimestampSchema,
} as const;

export const canonicalNutritionEntryInputSchema = z.object(canonicalNutritionEntryFields);

export type CanonicalNutritionEntryInput = z.infer<typeof canonicalNutritionEntryInputSchema>;

export const canonicalNutritionEntrySchema = canonicalNutritionEntryInputSchema.extend({
  requiresReview: z.boolean(),
});

export type CanonicalNutritionEntry = z.infer<typeof canonicalNutritionEntrySchema>;

export const canonicalNutritionEntryArraySchema = z.array(canonicalNutritionEntrySchema);

export const canonicalNutritionEntryDraftInputSchema = canonicalNutritionEntryInputSchema.extend({
  mealType: z.enum(mealTypes),
});

export type CanonicalNutritionEntryDraftInput = z.infer<
  typeof canonicalNutritionEntryDraftInputSchema
>;

export const canonicalNutritionEntryDraftInputArraySchema = z.array(
  canonicalNutritionEntryDraftInputSchema,
);

export const canonicalNutritionEntryDraftSchema = canonicalNutritionEntryDraftInputSchema.extend({
  requiresReview: z.boolean(),
});

export type CanonicalNutritionEntryDraft = z.infer<typeof canonicalNutritionEntryDraftSchema>;

export const canonicalNutritionEntryDraftArraySchema = z.array(canonicalNutritionEntryDraftSchema);

function collapseWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function normalizeQuantity(quantity: CanonicalQuantity): CanonicalQuantity {
  return {
    amount: quantity.amount,
    unit: collapseWhitespace(quantity.unit).toLowerCase(),
  };
}

function normalizeMacroTotals(macros: MacroTotals): MacroTotals {
  return {
    calories: macros.calories,
    proteinGrams: macros.proteinGrams,
    carbsGrams: macros.carbsGrams,
    fatGrams: macros.fatGrams,
    ...(macros.fiberGrams !== undefined ? { fiberGrams: macros.fiberGrams } : {}),
    ...(macros.sugarGrams !== undefined ? { sugarGrams: macros.sugarGrams } : {}),
    ...(macros.sodiumMilligrams !== undefined ? { sodiumMilligrams: macros.sodiumMilligrams } : {}),
  };
}

function normalizeCanonicalNutritionFields(
  entry: CanonicalNutritionEntryInput,
): CanonicalNutritionEntryInput {
  return {
    originalInput: entry.originalInput.trim(),
    normalizedName: collapseWhitespace(entry.normalizedName),
    inputQuantity: normalizeQuantity(entry.inputQuantity),
    canonicalQuantity: normalizeQuantity(entry.canonicalQuantity),
    macros: normalizeMacroTotals(entry.macros),
    source: entry.source,
    confidence: entry.confidence,
    consumedAt: new Date(entry.consumedAt).toISOString(),
  };
}

export function createEmptyMacroTotals(): MacroTotals {
  return {
    calories: 0,
    proteinGrams: 0,
    carbsGrams: 0,
    fatGrams: 0,
  };
}

export function sumMacroTotals(entries: Iterable<MacroTotals>): MacroTotals {
  const totals = createEmptyMacroTotals();

  for (const entry of entries) {
    totals.calories += entry.calories;
    totals.proteinGrams += entry.proteinGrams;
    totals.carbsGrams += entry.carbsGrams;
    totals.fatGrams += entry.fatGrams;
    totals.fiberGrams = (totals.fiberGrams ?? 0) + (entry.fiberGrams ?? 0);
    totals.sugarGrams = (totals.sugarGrams ?? 0) + (entry.sugarGrams ?? 0);
    totals.sodiumMilligrams = (totals.sodiumMilligrams ?? 0) + (entry.sodiumMilligrams ?? 0);
  }

  return totals;
}

function scaleNullableMacro(value: number | undefined, factor: number): number | undefined {
  return value === undefined ? undefined : value * factor;
}

export function scaleMacroTotals(macros: MacroTotals, factor: number): MacroTotals {
  if (!Number.isFinite(factor) || factor < 0) {
    throw new Error('Macro scale factor must be a non-negative finite number.');
  }

  return {
    calories: macros.calories * factor,
    proteinGrams: macros.proteinGrams * factor,
    carbsGrams: macros.carbsGrams * factor,
    fatGrams: macros.fatGrams * factor,
    ...(macros.fiberGrams !== undefined
      ? { fiberGrams: scaleNullableMacro(macros.fiberGrams, factor) }
      : {}),
    ...(macros.sugarGrams !== undefined
      ? { sugarGrams: scaleNullableMacro(macros.sugarGrams, factor) }
      : {}),
    ...(macros.sodiumMilligrams !== undefined
      ? { sodiumMilligrams: scaleNullableMacro(macros.sodiumMilligrams, factor) }
      : {}),
  };
}

export function shouldRequireNutritionReview(
  entry: Pick<CanonicalNutritionEntryInput, 'confidence' | 'source'>,
): boolean {
  return entry.confidence !== 'high' || entry.source === 'ai-estimate';
}

export function normalizeCanonicalNutritionEntry(
  input: CanonicalNutritionEntryInput,
): CanonicalNutritionEntry {
  const parsed = canonicalNutritionEntryInputSchema.parse(input);
  const normalized = normalizeCanonicalNutritionFields(parsed);

  return {
    ...normalized,
    requiresReview: shouldRequireNutritionReview(normalized),
  };
}

export function normalizeCanonicalNutritionEntryDraft(
  input: CanonicalNutritionEntryDraftInput,
): CanonicalNutritionEntryDraft {
  const parsed = canonicalNutritionEntryDraftInputSchema.parse(input);
  const normalized = normalizeCanonicalNutritionEntry(parsed);

  return {
    ...normalized,
    mealType: parsed.mealType,
  };
}
